/**
 * F1 — per-operation Graph retry policy. Per decisions.md §D1 + throttling-and-retry.md.
 *
 * Every public function in `lib/graph/*` wraps its Graph call with one of
 * READ_POLICY / WRITE_POLICY / PROBE_POLICY via `withRetry(policy, fn)`.
 * The graphClient has NO retry middleware — this wrapper is the only
 * retry layer (verified in graphClient.ts:19; Client.initWithMiddleware
 * accepts only authProvider). Two retry layers would compound (5 × 2 = 10
 * retries on writes) and obscure debugging.
 *
 * Exhaustion behaviour:
 * - READ:  throw RetryExhaustedError. UI surfaces via useGraphErrorToast.
 * - WRITE: throw RetryExhaustedError (F1). F1b will swap this for an
 *          IndexedDB local-edit queue per offline-and-resilience.md.
 * - PROBE: throw GraphProbeFailedError. AccessBadge UI shows ⚠️ Unknown.
 *
 * Graph's 429/503 responses set `Retry-After` (seconds). We honor it as a
 * MINIMUM wait; jitter adds on top so concurrent clients don't lockstep
 * into the same retry slot. Capped at 30s per attempt so a misbehaving
 * server can't pin a user's UI forever.
 *
 * Memory: feedback_audit_writer_pattern.md (same "leaf module + injected
 * deps" ethos). This module imports nothing React/Convex — leaf only.
 */

export type OnExhaust = 'throw' | 'enqueue';

export interface RetryPolicy {
  readonly name: 'READ' | 'WRITE' | 'PROBE';
  readonly maxRetries: number;
  readonly baseMs: number;
  readonly onExhaust: OnExhaust;
}

export const READ_POLICY: RetryPolicy = {
  name: 'READ',
  maxRetries: 2,
  baseMs: 250,
  onExhaust: 'throw',
};

export const WRITE_POLICY: RetryPolicy = {
  name: 'WRITE',
  maxRetries: 5,
  baseMs: 250,
  // F1b: enqueue to localEditQueue on exhaust (offline-and-resilience.md).
  // The registered enqueue handler is responsible for surfacing the
  // "queued for retry" toast and starting the drain worker if needed.
  // If no handler is registered, withRetry falls back to throwing
  // RetryExhaustedError so behaviour pre-F1b is preserved when callers
  // forget to wire the queue.
  onExhaust: 'enqueue',
};

export const PROBE_POLICY: RetryPolicy = {
  name: 'PROBE',
  maxRetries: 1,
  baseMs: 250,
  onExhaust: 'throw',
};

const MAX_BACKOFF_MS = 30_000;

export class RetryExhaustedError extends Error {
  constructor(
    public readonly policy: RetryPolicy,
    public readonly lastStatus: number | undefined,
    public readonly attempts: number,
    public readonly cause: unknown,
  ) {
    const status = lastStatus !== undefined ? `HTTP ${lastStatus}` : 'unknown error';
    super(
      `Graph ${policy.name} retry exhausted after ${attempts} attempts (${status}). ` +
        `Last error: ${cause instanceof Error ? cause.message : String(cause)}`,
    );
    this.name = 'RetryExhaustedError';
  }
}

export class GraphProbeFailedError extends Error {
  constructor(
    public readonly lastStatus: number | undefined,
    public readonly cause: unknown,
  ) {
    const status = lastStatus !== undefined ? `HTTP ${lastStatus}` : 'unknown error';
    super(
      `Graph permission probe failed (${status}). UI should render ⚠️ Unknown.`,
    );
    this.name = 'GraphProbeFailedError';
  }
}

interface ErrorWithStatus {
  statusCode?: number;
  status?: number;
  body?: unknown;
  responseHeaders?: { get?(name: string): string | null } | Record<string, string>;
  headers?: { get?(name: string): string | null } | Record<string, string>;
}

/** Status codes worth retrying. 408/425/429/500/502/503/504. */
function isRetryableStatus(status: number | undefined): boolean {
  if (status === undefined) return true; // network-level error — retry
  if (status === 408) return true;
  if (status === 425) return true;
  if (status === 429) return true;
  if (status >= 500 && status <= 504) return true;
  return false;
}

function pickStatus(err: unknown): number | undefined {
  const e = err as ErrorWithStatus;
  return e?.statusCode ?? e?.status;
}

/** Read `Retry-After` header (seconds) from any of the shapes Graph SDK exposes. */
function readRetryAfterSeconds(err: unknown): number | undefined {
  const e = err as ErrorWithStatus;
  const src = e?.responseHeaders ?? e?.headers;
  if (!src) return undefined;
  // Headers-like API (Fetch Response.headers)
  if (typeof (src as { get?: unknown }).get === 'function') {
    const raw = (src as { get: (n: string) => string | null }).get('Retry-After');
    if (!raw) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  }
  // Plain object
  const rec = src as Record<string, string>;
  const raw = rec['Retry-After'] ?? rec['retry-after'];
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/** Pure backoff math: 250ms × 2^n + jitter, capped. Exported for tests. */
export function nextBackoffMs(
  attempt: number,
  baseMs: number,
  rand: () => number = Math.random,
): number {
  const base = Math.min(baseMs * 2 ** attempt, MAX_BACKOFF_MS);
  const jitter = rand() * base;
  return Math.min(base + jitter, MAX_BACKOFF_MS);
}

/** Sleep with overridable timer (tests pass vi.fn()). */
async function sleep(
  ms: number,
  setTimeoutImpl: (cb: () => void, ms: number) => unknown = setTimeout,
): Promise<void> {
  return new Promise((resolve) => setTimeoutImpl(resolve, ms));
}

export interface EnqueueHint {
  /** Pre-classified so the queue can route 412→ConflictDialog etc. */
  errorKind: 'conflict' | 'access-denied' | 'transient' | 'fatal';
  /** Raw last error for forensic context (kept in the toast / row.lastErrorMessage). */
  lastError: unknown;
  /** HTTP status if known. */
  lastStatus: number | undefined;
}

export type WriteEnqueueHandler = (hint: EnqueueHint) => void | Promise<void>;

let writeEnqueueHandler: WriteEnqueueHandler | null = null;

/**
 * Register a handler called by `withRetry` when `WRITE_POLICY` exhausts.
 * Wire once at SPA boot (main.tsx) to the localEditQueue + toast. Calling
 * twice replaces the prior handler; tests can pass `null` to clear.
 *
 * The handler is responsible for capturing the pending payload (typically
 * via a closure inside the callsite-specific wrapper) and pushing it to
 * the queue. `withRetry` itself doesn't know the payload — keeping that
 * coupling here would force every leaf to take a queue dep.
 */
export function setWriteEnqueueHandler(handler: WriteEnqueueHandler | null): void {
  writeEnqueueHandler = handler;
}

/** Test-only access to the current handler. */
export function __getWriteEnqueueHandlerForTests(): WriteEnqueueHandler | null {
  return writeEnqueueHandler;
}

export interface WithRetryOpts {
  /** Override rng for deterministic backoff in tests. */
  rand?: () => number;
  /** Override timer (mostly for tests). */
  setTimeoutImpl?: (cb: () => void, ms: number) => unknown;
  /**
   * Per-call enqueue handler that takes precedence over the global one.
   * Used by sharepoint.ts wrappers that need to capture the payload
   * (driveId/itemId/body/etag) for queueing on exhaust.
   */
  enqueueOnExhaust?: WriteEnqueueHandler;
}

function classifyErrorKind(status: number | undefined): EnqueueHint['errorKind'] {
  if (status === 409 || status === 412) return 'conflict';
  if (status === 401 || status === 403) return 'access-denied';
  if (status === undefined || status === 408 || status === 425 || status === 429 || (status >= 500 && status <= 504)) {
    return 'transient';
  }
  return 'fatal';
}

/**
 * Run `fn` with retries per `policy`. Honors `Retry-After` as a MINIMUM wait;
 * jittered exponential backoff on top so two clients don't lockstep.
 *
 * Non-retryable errors (4xx other than 408/425/429) throw immediately
 * without consuming the retry budget — e.g. a 403 means access denied, not
 * "try again."
 */
export async function withRetry<T>(
  policy: RetryPolicy,
  fn: () => Promise<T>,
  opts: WithRetryOpts = {},
): Promise<T> {
  let attempt = 0;
  let lastErr: unknown;
  let lastStatus: number | undefined;

  // Total tries = 1 + maxRetries (the first call isn't a "retry").
  const maxAttempts = 1 + policy.maxRetries;

  while (attempt < maxAttempts) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      lastStatus = pickStatus(err);

      if (!isRetryableStatus(lastStatus)) {
        // Fail-fast on 4xx that isn't 408/425/429. Don't burn budget.
        throw err;
      }

      attempt++;
      if (attempt >= maxAttempts) break;

      const retryAfterMs = (readRetryAfterSeconds(err) ?? 0) * 1000;
      const backoffMs = nextBackoffMs(attempt - 1, policy.baseMs, opts.rand);
      const waitMs = Math.min(Math.max(retryAfterMs, backoffMs), MAX_BACKOFF_MS);
      await sleep(waitMs, opts.setTimeoutImpl);
    }
  }

  if (policy.name === 'PROBE') {
    throw new GraphProbeFailedError(lastStatus, lastErr);
  }

  // WRITE_POLICY: per F1b, enqueue to localEditQueue on exhaust if a
  // handler is registered. Throw RetryExhaustedError after the handler
  // runs so callers still see the error (the handler is responsible for
  // surfacing the "queued" toast; the throw lets the call site know the
  // write didn't land synchronously).
  if (policy.onExhaust === 'enqueue') {
    const handler = opts.enqueueOnExhaust ?? writeEnqueueHandler;
    if (handler) {
      try {
        await handler({
          errorKind: classifyErrorKind(lastStatus),
          lastError: lastErr,
          lastStatus,
        });
      } catch {
        // Handler failures must not mask the original error. The handler
        // should surface its own errors via its own toast.
      }
    }
  }

  throw new RetryExhaustedError(policy, lastStatus, attempt, lastErr);
}
